import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';
import { ScrollerType } from 'app/data/Enums';
import { ScrollBehaviourManager } from 'app/behaviour/managers/ScrollBehaviourManager';

import { ScrollVelocityLockable } from 'app/contracts/Scroll';

import DelayHelper from 'utils/timers';
import fastdom from 'fastdom';

import { ScrollbarView } from './ScrollbarView';
import { ScrollbarEngine } from './ScrollbarEngine';
import { ScrollbarInput } from './ScrollbarInput';
import { device } from 'utils/device';

/**
 * Handles custom scrollbar and vertical dragging. Orchestrates the
 * per-concern sub-objects in this folder (see scrollbar.types.ts for the
 * decomposition pattern, established in src/app/views/work/). Keeps: the
 * lifecycle hooks (init / resize / tick / DOMReady), the full public surface
 * used by AppController / SiteTransitions / AboutController / UIController /
 * VerticalDragController / HistoryController / ProjectController /
 * NavMenuController / DragPanel / DragableVideo / InteractiveArea, every
 * Events.on(...) / addEventListener registration (handlers delegate inward),
 * the platform / scroller-type detection, focus / snapshot / pause
 * orchestration, the ResizeSensor razor and the ScrollBehaviourManager.
 */
export class ScrollbarController extends Controller implements ScrollVelocityLockable {
	public endFlagged: boolean = false;

	private _scrollerType: ScrollerType = ScrollerType.NATIVE;
	private _active: boolean = false;
	private _paused: boolean = false;
	private _dragging: boolean = false;

	private _verticalDragActive: boolean;
	private _verticalDragPointerType: string;
	private _scrollJSEventFlag: boolean;
	private _pixiScrollActive: boolean = true;

	private _$target: HTMLElement;
	private _$heightTarget: HTMLElement;
	private _$extraHeightTarget: HTMLElement;

	//Scrolling with bar
	private _eventIds: any = { move: null, up: null };


	//Behaviours
	private _scrollBehaviourManager: ScrollBehaviourManager;

	public isSafari = false;
	private isFirefox = false;
	public isMac = false;
	private isIOS = false;
	private isAndroid = false;

	//Per-concern sub-objects (see scrollbar.types.ts for the pattern):
	private _view: ScrollbarView;
	private _engine: ScrollbarEngine;
	private _input: ScrollbarInput;

	constructor(id: string) {
		super(id);

		this._view = new ScrollbarView(this.DOM, {
			getContainer: () => this.container,
			getHeightTarget: () => this._$heightTarget,
			getExtraHeightTarget: () => this._$extraHeightTarget,
			getScrollerType: () => this._scrollerType,
			getPlaneTargetY: () => this._engine.planeTargetY,
			setPlaneTargetY: (value: number) => (this._engine.planeTargetY = value),
			getMaxTargetScroll: () => this._engine.maxTargetScroll,
			setMaxTargetScroll: (value: number) => (this._engine.maxTargetScroll = value),
			onReactivate: () => {
				this._active = true;
			}
		});

		this._engine = new ScrollbarEngine({
			getScrollerType: () => this._scrollerType,
			getTarget: () => this._$target,
			isVerticalDragActive: () => this._verticalDragActive,
			syncBarToTargetY: (time?: number, targetY?: number, ease?: any) =>
				this._view.syncBarToTargetY(time, targetY, ease)
		});

		this._input = new ScrollbarInput({
			isActive: () => this._active,
			isPaused: () => this._paused,
			isDragging: () => this._dragging,
			isHideScroller: () => this._view.hideScroller,
			isFirefox: () => this.isFirefox,
			isSafari: () => this.isSafari,
			isMac: () => this.isMac,
			getInnerHeight: () => this._view.innerHeight,
			getPlaneTargetY: () => this._engine.planeTargetY,
			setPlaneTargetY: (value: number) => (this._engine.planeTargetY = value),
			getMaxTargetScroll: () => this._engine.maxTargetScroll,
			setEasePower: (value: number) => (this._engine.easePower = value),
			syncBarToTargetY: () => this._view.syncBarToTargetY()
		});
	}

	public get velocity(): number {
		return this._engine.velocity;
	}

	public get planeY(): number {
		return this._engine.planeY;
	}

	public get isNativeType(): boolean {
		return this._scrollerType === ScrollerType.NATIVE || this._scrollerType === ScrollerType.NATIVE_CUSTOM;
	}

	public get scrollerType(): ScrollerType {
		return this._scrollerType;
	}

	public get pixiScrollActive(): boolean {
		return this._pixiScrollActive;
	}

	private iOS() {
		return (
			['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
			// iPad on iOS 13 detection
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
		);
	}

	private isAndroidCheck() {
		return /Android|webOS/i.test(navigator.userAgent);
	}

	public init(): void {
		Events.on(AppEvents.APP_RESIZE, this.resize.bind(this));

		this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual';
		}

		this.isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
		this.isIOS = this.iOS();
		this.isAndroid = this.isAndroidCheck();

		let navigatorTarget = navigator && navigator.userAgentData ? navigator.userAgentData : navigator;
		let platform = 'unknown';
		if (navigatorTarget) {
			platform = navigatorTarget.platform || 'unknown';
		}

		this.isMac = (!this.isIOS && platform.indexOf('Mac') > -1) || platform == 'macOS';

		const $body = document.querySelector('body');
		if (this.isIOS) this.DOM.addClass($body, '-ios');
		if (this.isMac) this.DOM.addClass($body, '-mac');
		if (this.isSafari) this.DOM.addClass($body, '-safari');
		if (this.isFirefox) this.DOM.addClass($body, 'firefox');

		if (this.isSafari) {
			this._input.applySafariOverrides();
		}

		this._engine.init();
		this._scrollBehaviourManager = new ScrollBehaviourManager();
	}

	public resize(): void {
		if (!this._active) {
			return;
		}

		this._scrollBehaviourManager.resize();
		this._view.setSizing();
		this._view.resizeBar(0.3, false);

		DelayHelper.setTimeout(() => {
			this._view.setSizing();
		}, 500);

		DelayHelper.setTimeout(() => {
			this._view.setSizing();
		}, 1000);
	}

	/** Get scroll as ration of max scroll */
	public getProgress(): number {
		return this._engine.getProgress();
	}

	public getRealProgress(): number {
		return this._engine.getRealProgress();
	}

	/** Get scroll as a 0-1 value */
	public getScroll(): number {
		return this._engine.getScroll();
	}

	public getRealScroll(): number {
		return this._engine.getRealScroll();
	}

	public getPageTweenTime(): number {
		return this._engine.getPageTweenTime();
	}

	public tweenTo(
		y: number,
		time: number = -1,
		ease: any = null,
		onCompleteTween: () => void = null,
		onUpdateTween: (planeScroll) => void = null
	): void {
		this._engine.tweenTo(y, time, ease, onCompleteTween, onUpdateTween);
	}

	public killScroll() {
		this._engine.killScroll();
	}

	/** Get scroll */

	public scrollTo(y: number): void {
		this._engine.scrollTo(y);
	}

	/** Scroll to an selector */
	public scrollToSelector(selector: string): void {
		let element: HTMLElement = this._$target.querySelector(selector);
		let y: number = this._getElementY(element);
		this.scroll(y);
	}

	/** reset */
	public reset(): void {
		this.setPause(false);
		this._view.barY = this._engine.planeTargetY = this._engine.planeY = this._engine.previousY = 0;
	}

	/** Set pause state (To allow interaction or not) */
	public setPause(pause: boolean): void {
		this._paused = pause;
	}

	/** Set Active */
	public setActive(active: boolean): void {
		this._active = active;
		if (active) {
			this.DOM.addClass(this.container, '-active');
		} else {
			this.DOM.removeClass(this.container, '-active');
		}
	}

	public getActive(): boolean {
		return this._active;
	}

	public getDirection(): number {
		return this._engine.direction;
	}

	/** Called as the vertical drag is used */
	public verticalDrag(amount: number): void {
		this._engine.verticalDrag(amount);
	}

	public shouldResizeLock(): boolean {
		return false;
		return this.isIOS || this.isAndroid;
	}

	public removeResidule(): void {
		this._engine.planeTargetY = this._engine.planeY;
	}

	/** Called after a vertical drag is release  */
	public throwPage(yVelocity: number): void {
		this._engine.throwPage(yVelocity);
	}

	/** Focus a new section */
	public setFocus(
		$element: HTMLElement,
		animateTime: number = 0,
		$heightTarget: HTMLElement = null,
		resetScroll: boolean = true
	): void {
		let reactivateAfterResize: boolean = false;
		this.endFlagged = false;
		this._$target = $element;
		this._$heightTarget = $heightTarget ? $heightTarget : $element;

		if (this._$target) {
			//If animating between targets then decative while animation is working:
			if (this._active && animateTime !== 0) {
				this._active = false;
				reactivateAfterResize = true;
			}

			if (resetScroll) {
				this._view.barY = this._engine.planeTargetY = this._engine.planeY = 0;
			}

			this._view.setSizing();
			this._view.resizeBar(animateTime, reactivateAfterResize);

			//Set the container for the behaviours:
			this._scrollBehaviourManager.setContainer(this._$target);

			//If there are modules that neeed to be created do so:
			this.module.createModulesInTarget(this._$target);

			//If the sldier is custom type, then track resizing on the actual alement with ResizeSensor class
			if (
				!this._razor &&
				(this._scrollerType === ScrollerType.CUSTOM || this._scrollerType === ScrollerType.NATIVE_CUSTOM)
			) {
				var razor: any = (window as any).ResizeSensor;
				this._razor = new razor(this._$heightTarget, () => this.resize());
			}

			setTimeout(() => {
				this.resize();
			}, 1000);
		} else {
			this._active = false;
			this._engine.planeY = this._view.barY = this._engine.planeTargetY = 0;
			this._scrollBehaviourManager.setContainer(null);
		}

		this.scroll(0);
		this._engine.planeY = this._view.barY = this._engine.planeTargetY = this._engine.lastScroll = 0;
	}

	private _razor;

	//** Tick - update scroll position, as there is inernia applied to the planes movement */
	public tick(): void {
		if (!this._active) {
			return;
		}

		//Updatet the y position of scroll panel in occordance

		if (this._canProcessTick()) {
			this._engine.updateTargetScroll();
			this._input.reduceScrollWheelRecord();
		}

		this._scrollBehaviourManager.updateScroll(Math.abs(this._engine.planeY));

		this._engine.calculateVelocity();
	}

	private _canProcessTick(): boolean {
		if (this._engine.tweening) {
			return false;
		}

		//Always process custom / native custom as they take over scrolling
		if (this._scrollerType === ScrollerType.CUSTOM || this._scrollerType === ScrollerType.NATIVE_CUSTOM) {
			return true;
		}

		//Native type only needs to be taken over when dragging and is mouse
		return this._verticalDragActive && this._verticalDragPointerType === 'mouse';
	}

	/** Scrollbar will always be in global HTML so use DOMReady event to setup */
	public DOMReady(): void {
		this._testScrollerType();
		this._scrollBehaviourManager.setType(this._scrollerType);

		this._view.queryRefs(this.container);

		//https://medium.com/@joelmalone/prevent-edge-swipe-gestures-in-your-html-game-but-only-in-safari-fba815a529a2
		function preventBrowserHistorySwipeGestures() {
			function touchStart(ev: TouchEvent) {
				if (ev.touches.length === 1) {
					const touch = ev.touches[0];
					if (touch.clientX < window.innerWidth * 0.1 || touch.clientX > window.innerWidth * 0.9) {
						console.log('touchtarget', ev.target as HTMLElement);

						if (!(ev.target as HTMLElement).dataset.allowtouch) {
							ev.preventDefault();
						}
					}
				}
			}

			// Safari defaults to passive: true for the touchstart event, so we need  to explicitly specify false
			// See https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
			const options = { passive: false };

			window.addEventListener('touchstart', touchStart, options);
		}

		preventBrowserHistorySwipeGestures();

		document.addEventListener(
			'wheel',
			(e: WheelEvent) => {
				this._input.onMouseWheel(e);
			},
			false
		);

		if (this._scrollerType === ScrollerType.CUSTOM || this._scrollerType === ScrollerType.NATIVE_CUSTOM) {
			Events.on('mousedown', '.scrollbar__bar', this._onBarClick.bind(this));
			Events.on('touchstart', window, this._onTouchStart.bind(this));

			//Add mouse wheel events:

			//Add Keyboard events:
			window.addEventListener(
				'keyup',
				(e: KeyboardEvent) => {
					this._input.onKeyPress(e);
				},
				false
			);
		} else {
			//When
			window.addEventListener('scroll', (e) => {
				if (!this._scrollJSEventFlag) {
				}
			});
			window.addEventListener('DOMMouseScroll', (e) => {
				this._cancelNativeVerticalDrag();
			});
			window.addEventListener(
				'keyup',
				(e: KeyboardEvent) => {
					this._input.onKeyPress(e);
				},
				false
			);
		}

		//Temp to test
	}

	public setVerticalDragActive(active: boolean, pointerType: string) {
		if (globals.tmpApp) {
			return;
		}

		this._verticalDragActive =
			this._scrollerType !== ScrollerType.NATIVE ||
			(this._scrollerType === ScrollerType.NATIVE && pointerType == 'mouse');
		this._verticalDragPointerType = pointerType;
	}

	public scroll(y: number) {
		this._engine.scroll(y);
	}

	/**
	 * Interactivity will be locked at a certian velocity to stop drag type interaction
	 * Most likely called by /library/scroll/InterativeArea.ts class on mouseover event
	 */
	public scrollVelocityLock(onUnlock: Function): boolean {
		return this._engine.scrollVelocityLock(onUnlock);
	}

	public resetToZero() {
		console.trace('window.scroll 0,0');

		fastdom.mutate(() => {
			window.scroll(0, 0);
		});
	}

	public hardScroll(y) {
		this._engine.hardScroll(y);
	}

	public resetMovement(): void {
		this._engine.planeTargetY = this._engine.planeY;
	}

	public getMaxScroll() {
		return this._engine.maxTargetScroll;
	}

	public resetTargetToZero($target) {
		this._engine.resetTargetToZero($target);
	}

	//private
	//____________________________________________________________________________________
	//____________________________________________________________________________________
	//____________________________________________________________________________________
	//____________________________________________________________________________________

	/** Bar click event */
	private _onBarClick(e: MouseEvent): void {
		if (!this._active || this._paused || this._view.hideScroller) {
			return;
		}

		this._engine.easePower = 2;
		this._view.startBarDrag(e.clientY);

		this.DOM.addClass(this.container, '-dragging');
		this._dragging = true;

		this._eventIds.move = Events.on('mousemove', window, this._onBarDrag.bind(this));
		this._eventIds.up = Events.on('mouseup', window, this._onBarRelease.bind(this));
	}

	/** On drag of bar update the scroll position */
	private _onBarDrag(e: MouseEvent): void {
		this._view.barDrag(e.clientY);
	}

	//** On release of drag bar */
	private _onBarRelease(): void {
		Events.offById(this._eventIds.move, this._eventIds.up);
		this.DOM.removeClass(this.container, '-dragging');
		this._dragging = false;
	}

	private _onTouchStart(e: TouchEvent): void {
		if (!this._active || this._paused || this._view.hideScroller) {
			return;
		}

		this._dragging = true;
		this._input.onTouchStart(e);
		this._eventIds.moveTouch = Events.on('touchmove', window, this._onTouchMove.bind(this));
		this._eventIds.moveTouchUp = Events.on('touchend', window, this._onTouchEnd.bind(this));
	}

	private _onTouchMove(e: TouchEvent): void {
		this._input.onTouchMove(e);
	}

	private _onTouchEnd(e: TouchEvent): void {
		Events.offById(this._eventIds.moveTouch, this._eventIds.moveTouchUp);
		this._dragging = false;
		this._input.onTouchEnd(e);
	}

	private _getElementY(element: HTMLElement): number {
		if (!element) {
			try {
				throw new Error('Target element does not exsist in target conatiner');
			} catch (e) {
				console.log(e);
			}
			return 0;
		}

		return element.offsetTop;
	}



	/** Test the scroller type */
	private _testScrollerType(): void {
		if (this.isIOS && device.innerWidth < 900) {
			this._scrollerType = ScrollerType.NATIVE;
		} else if (this.isAndroid && device.innerWidth < 900) {
			this._scrollerType = ScrollerType.NATIVE;
		} else if (this.isIOS || this.isAndroid) {
			//Tablets / ipads /etc
			this._scrollerType = ScrollerType.NATIVE;
		} else if (this.isSafari) {
			this._scrollerType = ScrollerType.NATIVE;
		} else if (this.isMac) {
			//Tablets / ipads /etc
			this._scrollerType = ScrollerType.NATIVE_CUSTOM;
		} else {
			//WINDOWS / OTHER ??
			this._scrollerType = ScrollerType.NATIVE_CUSTOM;
		}

		if (globals.tmpApp) {
			this._scrollerType = ScrollerType.NATIVE;
		}

		//If native scrolling and not MAC then disable the fixed position PIXI canvas and allow parallax scrolling in project:
		if (this._scrollerType == ScrollerType.NATIVE && !this.isMac) {
			this._pixiScrollActive = false;
			this.DOM.addClass(document.querySelector('body'), '-nopixiscroll');
			this.DOM.addClass(document.querySelector('html'), '-nopixiscroll');
		}

		if (this._scrollerType === ScrollerType.NATIVE) {
			this.DOM.addClass(document.querySelector('body'), '-nativescroller');
			this.DOM.addClass(document.querySelector('html'), '-nativescroller');
		}
		if (this._scrollerType === ScrollerType.NATIVE_CUSTOM) {
			this.DOM.addClass(document.querySelector('body'), '-nativecustomscroller');
		}
		//(A ScrollerType.CUSTOM -> body.-customscroller branch used to sit here,
		//but no branch above has produced CUSTOM for a long time — phones went
		//NATIVE — so it never fired, and no stylesheet targets -customscroller.
		//If CUSTOM is ever assigned again, restore the class-add too.)
	}

	private _cancelNativeVerticalDrag(): void {
		this._verticalDragActive = false;
	}
}
